<template>
  <v-container id="product-details" fluid class="pt-0">
    <v-row dense align="start" class="mt-0 pt-0">
      <v-col cols="12" md="6" class="pt-4">
        <v-row dense>
          <v-col cols="12" class="p-bottom-border pb-4 pt-0">
            <p class="g-bold g-heading-xs g-text-color-black mb-1">
              Trading & Category Hierarchy
            </p>
            <v-chip class="p-chip" small>{{
              item.categoryProductHierarchy.qualifiedName
            }}</v-chip>
          </v-col>
        </v-row>
        <v-row dense class="mt-0">
          <v-col cols="6" class="p-bottom-border py-2">
            <div class="mb-3">
              <p class="g-bold g-heading-xs g-text-color-black mb-1">
                Product Type
              </p>
              <v-chip
                v-if="item.productType === 'GROCERY'"
                class="p-chip-product-type"
                small
              >
                <v-icon class="mr-1" small>mdi-baguette</v-icon
                ><strong>Grocery & Fresh</strong>
              </v-chip>
              <v-chip
                v-if="item.productType === 'LIQUOR'"
                class="p-chip-product-type"
                small
              >
                <v-icon class="mr-1" small>mdi-glass-wine</v-icon
                ><strong>Liquor</strong>
              </v-chip>
              <v-chip
                v-if="item.productType === 'SERVICE'"
                class="p-chip-product-type"
                small
              >
                <v-icon class="mr-1" small>mdi-broom</v-icon>
                <strong>Service</strong>
              </v-chip>
            </div>
            <v-row
              v-if="displayRules.showApplicableTaxCodes"
              dense
              class="mb-2"
            >
              <v-col cols="6">
                <p class="g-bold g-heading-xs g-text-color-black">
                  Applied taxes
                </p>
                <div v-if="item.hasGst || item.hasWet">
                  <v-chip
                    color="primary"
                    class="ml-0 ma-1"
                    v-if="item.hasGst"
                    small
                    >{{ displayRules.primaryTaxLabel }}</v-chip
                  >
                  <v-chip
                    color="primary"
                    class="ml-0 ma-1"
                    v-if="item.hasWet"
                    small
                    >{{ displayRules.wetLabel }}</v-chip
                  >
                </div>
                <div v-else>
                  <span>No taxes applied</span>
                </div>
              </v-col>
              <v-col cols="6">
                <div
                  class="mb-2 text-right pr-3"
                  v-if="
                    ownerControlledProductTradingTerms &&
                    (item.wasOrIsLive || item.status === 'LIVE_FUTURE')
                  "
                >
                  <p class="g-bold g-heading-xs g-text-color-black">
                    Product Trading Terms
                  </p>
                  <v-chip class="p-chip" small>{{
                    $filters.percentage(item.tradingTermsPercentage)
                  }}</v-chip>
                </div>
              </v-col>
            </v-row>
            <div v-if="isUSCountry" class="mb-3">
              <p class="g-bold g-heading-xs g-text-color-black mb-1">
                US Tax Code (TIC)
              </p>
              <v-chip v-if="formattedUsTaxCode" class="p-chip" small>
                {{ formattedUsTaxCode }}
              </v-chip>
              <span v-else>Not specified</span>
            </div>
          </v-col>
          <v-col cols="6" class="p-right-column p-bottom-border py-2">
            <div class="mb-3">
              <p class="g-bold g-heading-xs g-text-color-black mb-1">
                Target Type
              </p>
              <v-chip
                v-if="targetType"
                class="p-chip"
                :class="{
                  'p-chip-banner': targetType === BANNER_GROUP,
                  'p-chip-mso': targetType === MULTI_STORE_OPERATOR,
                  'p-chip-retailer': targetType === RETAILER
                }"
                small
                >{{ targetType }}</v-chip
              >
              <span v-else>Not specified</span>
            </div>

            <div
              v-if="
                [BANNER_GROUP, MULTI_STORE_OPERATOR, RETAILER].includes(
                  targetType
                )
              "
              class="mb-3"
            >
              <p class="g-bold g-heading-xs g-text-color-black mb-1">
                Selected {{ targetType }}
              </p>
              <v-chip
                class="mr-1 mb-1"
                color="primary"
                v-for="item in targetList"
                :key="item"
                small
              >
                {{ item }}
              </v-chip>
            </div>

            <template v-if="!isServiceTypeProduct">
              <p class="g-bold g-heading-xs g-text-color-black pt-1">Sold as</p>
              <span v-if="item.variableWeightTargetInKg">Variable Weight</span>
              <span v-else-if="item.packageType === 'PALLET'">Pallet</span>
              <span v-else>Fixed Weight</span>
            </template>
          </v-col>

          <v-col cols="12" md="6" class="pt-2">
            <template v-if="!!item.familyGroup">
              <p class="g-bold g-heading-xs g-text-color-black">
                Family Group Name
              </p>
              <div class="mt-1 mb-4">
                <v-chip outlined dense class="mr-1 mb-2" color="primary" small>
                  <router-link
                    class="i-pill-link"
                    :to="{
                      name: 'family-groups',
                      query: { familyGroupId: item.familyGroup.id }
                    }"
                  >
                    {{ item.familyGroup.name }}
                  </router-link>
                </v-chip>
              </div>
            </template>
            <template
              v-if="
                promoByStatus(item.activeOrFuturePriceDrops, 'ACTIVE').length
              "
            >
              <p class="g-bold g-heading-xs g-text-color-black mb-2">
                Active Price Drops
              </p>
              <div class="mt-1 mb-4">
                <v-chip
                  v-for="priceDrop in promoByStatus(
                    item.activeOrFuturePriceDrops,
                    'ACTIVE'
                  )"
                  class="mr-1 mb-2 pl-1"
                  :class="[
                    { 'p-chip-family-group-promotion': priceDrop.familyGroup },
                    { 'p-chip-single-product-promotion': priceDrop.productCode }
                  ]"
                  color="primary"
                  dense
                  :key="priceDrop.id"
                  outlined
                  small
                >
                  <v-icon
                    v-if="priceDrop.familyGroup"
                    class="i-promo-type-icon--small i-promo-type-icon-family-group mr-1"
                    v-tooltip="'Family Group Price Drop'"
                    x-small
                  >
                    mdi-alpha-f-circle-outline
                  </v-icon>
                  <v-icon
                    v-else-if="priceDrop.productCode"
                    class="i-promo-type-icon--small i-promo-type-icon-single-product mr-1"
                    v-tooltip="'Product Price Drop'"
                    x-small
                  >
                    mdi-alpha-p-circle-outline
                  </v-icon>

                  <router-link
                    :to="{ name: 'price-drops', query: { id: priceDrop.id } }"
                    class="i-pill-link"
                    >{{ $filters.formatDate(priceDrop.startDate) }} ~
                    {{ $filters.formatDate(priceDrop.endDate) }}
                  </router-link>
                </v-chip>
              </div>
            </template>
            <template
              v-if="
                promoByStatus(item.activeOrFuturePriceDrops, 'UPCOMING').length
              "
            >
              <p class="g-bold g-heading-xs g-text-color-black mb-2">
                Upcoming Price Drops
              </p>
              <div class="mt-1 mb-4">
                <v-chip
                  v-for="priceDrop in promoByStatus(
                    item.activeOrFuturePriceDrops,
                    'UPCOMING'
                  )"
                  class="mr-1 mb-2 pl-1"
                  :class="[
                    { 'p-chip-family-group-promotion': priceDrop.familyGroup },
                    { 'p-chip-single-product-promotion': priceDrop.productCode }
                  ]"
                  dense
                  :key="priceDrop.id"
                  outlined
                  small
                >
                  <v-icon
                    v-if="priceDrop.familyGroup"
                    class="i-promo-type-icon--small i-promo-type-icon-family-group mr-1"
                    v-tooltip="'Family Group Price Drop'"
                    x-small
                  >
                    mdi-alpha-f-circle-outline
                  </v-icon>
                  <v-icon
                    v-else-if="priceDrop.productCode"
                    class="i-promo-type-icon--small i-promo-type-icon-single-product mr-1"
                    v-tooltip="'Product Price Drop'"
                    x-small
                  >
                    mdi-alpha-p-circle-outline
                  </v-icon>

                  <router-link
                    :to="{ name: 'price-drops', query: { id: priceDrop.id } }"
                    class="i-pill-link"
                    >{{ $filters.formatDate(priceDrop.startDate) }} ~
                    {{ $filters.formatDate(priceDrop.endDate) }}
                  </router-link>
                </v-chip>
              </div>
            </template>
            <template
              v-if="
                promoByStatus(item.activeOrFuturePromotions, 'ACTIVE').length
              "
            >
              <p class="g-bold g-heading-xs g-text-color-black mb-2">
                Active Specials
              </p>
              <div class="mt-1 mb-4">
                <v-chip
                  v-for="special in promoByStatus(
                    item.activeOrFuturePromotions,
                    'ACTIVE'
                  )"
                  class="mr-1 mb-2 pl-1"
                  :class="[
                    { 'p-chip-family-group-promotion': special.familyGroup },
                    { 'p-chip-single-product-promotion': special.productCode }
                  ]"
                  dense
                  :key="special.id"
                  outlined
                  small
                >
                  <v-icon
                    v-if="special.familyGroup"
                    class="i-promo-type-icon--small i-promo-type-icon-family-group mr-1"
                    v-tooltip="'Family Group Special'"
                    x-small
                  >
                    mdi-alpha-f-circle-outline
                  </v-icon>
                  <v-icon
                    v-else-if="special.productCode"
                    class="i-promo-type-icon--small i-promo-type-icon-single-product mr-1"
                    v-tooltip="'Product Special'"
                    x-small
                  >
                    mdi-alpha-p-circle-outline
                  </v-icon>

                  <router-link
                    :to="{ name: 'specials', query: { id: special.id } }"
                    class="i-pill-link"
                    >{{ $filters.formatDate(special.startDate) }} ~
                    {{ $filters.formatDate(special.endDate) }}
                  </router-link>
                </v-chip>
              </div>
            </template>
            <template
              v-if="
                promoByStatus(item.activeOrFuturePromotions, 'UPCOMING').length
              "
            >
              <p class="g-bold g-heading-xs g-text-color-black mb-2">
                Upcoming Specials
              </p>
              <div class="mt-1 mb-4">
                <v-chip
                  v-for="special in promoByStatus(
                    item.activeOrFuturePromotions,
                    'UPCOMING'
                  )"
                  class="mr-1 mb-2 pl-1"
                  :class="[
                    { 'p-chip-family-group-promotion': special.familyGroup },
                    { 'p-chip-single-product-promotion': special.productCode }
                  ]"
                  dense
                  :key="special.id"
                  outlined
                  small
                >
                  <v-icon
                    v-if="special.familyGroup"
                    class="i-promo-type-icon--small i-promo-type-icon-family-group mr-1"
                    v-tooltip="'Family Group Special'"
                    x-small
                  >
                    mdi-alpha-f-circle-outline
                  </v-icon>
                  <v-icon
                    v-else-if="special.productCode"
                    class="i-promo-type-icon--small i-promo-type-icon-single-product mr-1"
                    v-tooltip="'Product Special'"
                    x-small
                  >
                    mdi-alpha-p-circle-outline
                  </v-icon>

                  <router-link
                    :to="{ name: 'specials', query: { id: special.id } }"
                    class="i-pill-link"
                    >{{ $filters.formatDate(special.startDate) }} ~
                    {{ $filters.formatDate(special.endDate) }}
                  </router-link>
                </v-chip>
              </div>
            </template>
            <template
              v-if="
                promoByStatus(item.activeOrFutureVolumePromotions, 'ACTIVE')
                  .length
              "
            >
              <h4 class="mb-2">Active Volume Buys</h4>
              <div class="mt-1 mb-4">
                <v-chip
                  v-for="volumeBuy in promoByStatus(
                    item.activeOrFutureVolumePromotions,
                    'ACTIVE'
                  )"
                  class="mr-1 mb-2 pl-1"
                  :class="[
                    { 'p-chip-family-group-promotion': volumeBuy.familyGroup },
                    { 'p-chip-single-product-promotion': volumeBuy.productCode }
                  ]"
                  dense
                  :key="volumeBuy.id"
                  outlined
                  small
                >
                  <v-icon
                    v-if="volumeBuy.familyGroup"
                    class="i-promo-type-icon--small i-promo-type-icon-family-group mr-1"
                    v-tooltip="'Family Group Volume Buy'"
                    x-small
                  >
                    mdi-alpha-f-circle-outline
                  </v-icon>
                  <v-icon
                    v-else-if="volumeBuy.productCode"
                    class="i-promo-type-icon--small i-promo-type-icon-single-product mr-1"
                    v-tooltip="'Product Volume Buy'"
                    x-small
                  >
                    mdi-alpha-p-circle-outline
                  </v-icon>

                  <router-link
                    :to="{ name: 'volume-buys', query: { id: volumeBuy.id } }"
                    class="i-pill-link"
                    >{{ volumeBuy.name }}
                  </router-link>
                </v-chip>
              </div>
            </template>
            <template
              v-if="
                promoByStatus(item.activeOrFutureVolumePromotions, 'UPCOMING')
                  .length
              "
            >
              <p class="g-bold g-heading-xs g-text-color-black mb-2">
                Upcoming Volume Buys
              </p>
              <div class="mt-1 mb-4">
                <v-chip
                  v-for="volumeBuy in promoByStatus(
                    item.activeOrFutureVolumePromotions,
                    'UPCOMING'
                  )"
                  class="mr-1 mb-2 pl-1"
                  :class="[
                    { 'p-chip-family-group-promotion': volumeBuy.familyGroup },
                    { 'p-chip-single-product-promotion': volumeBuy.productCode }
                  ]"
                  dense
                  :key="volumeBuy.id"
                  outlined
                  small
                >
                  <v-icon
                    v-if="volumeBuy.familyGroup"
                    class="i-promo-type-icon--small i-promo-type-icon-family-group mr-1"
                    v-tooltip="'Family Group Volume Buy'"
                    x-small
                  >
                    mdi-alpha-f-circle-outline
                  </v-icon>
                  <v-icon
                    v-else-if="volumeBuy.productCode"
                    class="i-promo-type-icon--small i-promo-type-icon-single-product mr-1"
                    v-tooltip="'Product Volume Buy'"
                    x-small
                  >
                    mdi-alpha-p-circle-outline
                  </v-icon>

                  <router-link
                    :to="{ name: 'volume-buys', query: { id: volumeBuy.id } }"
                    class="i-pill-link"
                    >{{ volumeBuy.name }}
                  </router-link>
                </v-chip>
              </div>
            </template>
            <template
              v-if="
                promoByStatus(item.activeOrFutureBuyGetFreePromotions, 'ACTIVE')
                  .length
              "
            >
              <p class="g-bold g-heading-xs g-text-color-black mb-2">
                Active Buy Get Free Promotions
              </p>
              <div class="mt-1 mb-4">
                <v-chip
                  v-for="buyGetFree in promoByStatus(
                    item.activeOrFutureBuyGetFreePromotions,
                    'ACTIVE'
                  )"
                  class="mr-1 mb-2 pl-1"
                  :class="[
                    { 'p-chip-family-group-promotion': buyGetFree.familyGroup },
                    {
                      'p-chip-single-product-promotion': buyGetFree.productCode
                    }
                  ]"
                  dense
                  :key="buyGetFree.id"
                  outlined
                  small
                >
                  <v-icon
                    v-if="buyGetFree.familyGroup"
                    class="i-promo-type-icon--small i-promo-type-icon-family-group mr-1"
                    v-tooltip="'Family Group Buy Get Free'"
                    x-small
                  >
                    mdi-alpha-f-circle-outline
                  </v-icon>
                  <v-icon
                    v-else-if="buyGetFree.productCode"
                    class="i-promo-type-icon--small i-promo-type-icon-single-product mr-1"
                    v-tooltip="'Product Buy Get Free'"
                    x-small
                  >
                    mdi-alpha-p-circle-outline
                  </v-icon>

                  <router-link
                    :to="{ name: 'buy-get-free', query: { id: buyGetFree.id } }"
                    class="i-pill-link"
                  >
                    {{ $filters.formatDate(buyGetFree.startDate) }} ~
                    {{ $filters.formatDate(buyGetFree.endDate) }}
                  </router-link>
                </v-chip>
              </div>
            </template>
            <template
              v-if="
                promoByStatus(
                  item.activeOrFutureBuyGetFreePromotions,
                  'UPCOMING'
                ).length
              "
            >
              <p class="g-bold g-heading-xs g-text-color-black mb-2">
                Upcoming Buy Get Free Promotions
              </p>
              <div class="mt-1 mb-4">
                <v-chip
                  v-for="buyGetFree in promoByStatus(
                    item.activeOrFutureBuyGetFreePromotions,
                    'UPCOMING'
                  )"
                  class="mr-1 mb-2 pl-1"
                  :class="[
                    { 'p-chip-family-group-promotion': buyGetFree.familyGroup },
                    {
                      'p-chip-single-product-promotion': buyGetFree.productCode
                    }
                  ]"
                  dense
                  :key="buyGetFree.id"
                  outlined
                  small
                >
                  <v-icon
                    v-if="buyGetFree.familyGroup"
                    class="i-promo-type-icon--small i-promo-type-icon-family-group mr-1"
                    v-tooltip="'Family Group Buy Get Free'"
                    x-small
                  >
                    mdi-alpha-f-circle-outline
                  </v-icon>
                  <v-icon
                    v-else-if="buyGetFree.productCode"
                    class="i-promo-type-icon--small i-promo-type-icon-single-product mr-1"
                    v-tooltip="'Product Buy Get Free'"
                    x-small
                  >
                    mdi-alpha-p-circle-outline
                  </v-icon>

                  <router-link
                    :to="{ name: 'buy-get-free', query: { id: buyGetFree.id } }"
                    class="i-pill-link"
                  >
                    {{ $filters.formatDate(buyGetFree.startDate) }} ~
                    {{ $filters.formatDate(buyGetFree.endDate) }}
                  </router-link>
                </v-chip>
              </div>
            </template>
          </v-col>
        </v-row>

        <div class="mt-2 mb-0">
          <p class="g-bold g-heading-xs g-text-color-black mb-2">
            Zone pricing
          </p>
          <v-row v-if="hasRegionalPrices">
            <v-simple-table class="p-zone-prices-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-center text-uppercase">Zones</th>

                    <th class="text-center text-uppercase">
                      <template v-if="isServiceTypeProduct">Price</template>
                      <template v-else>Carton Price</template>
                      <br />(ex Tax)
                    </th>

                    <th class="text-center text-uppercase">
                      <template v-if="item.variableWeightTargetInKg"
                        >Cost / KG<br />(ex Tax)</template
                      >
                      <template v-else-if="isServiceTypeProduct">
                        Cost<br />(ex Tax)
                      </template>
                      <template v-else>Unit Cost<br />(ex Tax)</template>
                    </th>

                    <th v-if="!isUSCountry" class="text-center text-uppercase">
                      <template v-if="item.variableWeightTargetInKg"
                        >Cost / KG<br />(inc Tax)</template
                      >
                      <template v-else-if="isServiceTypeProduct">
                        Cost<br />(inc Tax)
                      </template>
                      <template v-else>Unit Cost<br />(inc Tax)</template>
                    </th>

                    <th
                      class="text-center text-uppercase"
                      v-if="!isServiceTypeProduct && !isUSCountry"
                    >
                      <template v-if="item.variableWeightTargetInKg"
                        >RRP / KG<template v-if="!isUSCountry"
                          ><br />(inc Tax)</template
                        ></template
                      >
                      <template v-else
                        >Unit RRP<template v-if="!isUSCountry"
                          ><br />(inc Tax)</template
                        ></template
                      >
                    </th>

                    <th
                      v-if="!isServiceTypeProduct && !isUSCountry"
                      class="text-center text-uppercase"
                    >
                      GP%
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    :key="key"
                    v-for="(
                      [
                        {
                          price,
                          unitPriceExGst,
                          unitPriceIncTax,
                          pricePerKgExGst,
                          pricePerKgIncTax,
                          rrp,
                          grossProfitPercentage
                        }
                      ],
                      key
                    ) in sortedRegionalPriceMap"
                  >
                    <td class="text-center">
                      <strong>{{ getPricingRegionName(key) }}</strong>
                    </td>
                    <td class="text-center">{{ $filters.currency(price) }}</td>
                    <td class="i-td-chip">
                      <v-chip
                        class="p-chip"
                        :class="{
                          negative: unitPriceExGst < 0,
                          positive: unitPriceExGst > 0
                        }"
                        small
                      >
                        <template v-if="item.variableWeightTargetInKg">{{
                          $filters.currency(pricePerKgExGst)
                        }}</template>
                        <template v-else>{{
                          $filters.currency(unitPriceExGst)
                        }}</template>
                      </v-chip>
                    </td>
                    <td v-if="!isUSCountry" class="text-center">
                      <template v-if="item.variableWeightTargetInKg">{{
                        $filters.currency(pricePerKgIncTax)
                      }}</template>
                      <template v-else>{{
                        $filters.currency(unitPriceIncTax)
                      }}</template>
                    </td>
                    <td
                      v-if="!isServiceTypeProduct && !isUSCountry"
                      class="text-center"
                    >
                      {{ $filters.currency(rrp) }}
                    </td>
                    <td
                      v-if="!isServiceTypeProduct && !isUSCountry"
                      class="i-td-chip"
                    >
                      <v-chip
                        class="p-chip"
                        :class="{
                          negative: grossProfitPercentage < 0,
                          positive: grossProfitPercentage > 0
                        }"
                        small
                        >{{ grossProfitPercentage }}</v-chip
                      >
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-row>
          <div v-else>
            <span>No zones prices applied</span>
          </div>
        </div>
      </v-col>

      <v-col cols="12" md="6" class="p-right-column pt-4">
        <div class="mb-3">
          <p class="g-bold g-heading-xs g-text-color-black mb-2">
            Product Information
          </p>
          <span>{{ item.details || 'Not specified' }}</span>
        </div>

        <div v-if="!isServiceTypeProduct" class="mb-3">
          <p class="g-bold g-heading-xs g-text-color-black">Ingredients</p>
          <span>{{ item.ingredients || 'Not specified' }}</span>
        </div>

        <div v-if="!isServiceTypeProduct" class="mb-3">
          <p class="g-bold g-heading-xs g-text-color-black">
            Country of Origin
          </p>
          <span>
            <v-icon v-if="!!item.countryOfOrigin" class="p-icon mb-1" small
              >mdi-earth</v-icon
            >
            {{ item.countryOfOrigin || 'Not specified' }}
          </span>
        </div>

        <v-row v-if="isLiquorTypeProduct">
          <v-col cols="6" v-if="isLiquorTypeProduct">
            <p class="g-bold g-heading-xs g-text-color-black">
              State of Origin
            </p>
            <span>
              <v-icon
                v-if="!!item.liquorInformation.stateOfOrigin"
                class="p-icon mb-1"
                small
                >mdi-map-marker</v-icon
              >
              {{ item.liquorInformation.stateOfOrigin || 'Not specified' }}
            </span>
          </v-col>

          <v-col cols="6" v-if="isLiquorTypeProduct">
            <p class="g-bold g-heading-xs g-text-color-black">
              Region of Origin
            </p>
            <span>
              <v-icon
                v-if="!!item.liquorInformation.regionOfOrigin"
                class="p-icon mb-1"
                small
                >mdi-map-marker</v-icon
              >
              {{ item.liquorInformation.regionOfOrigin || 'Not specified' }}
            </span>
          </v-col>
        </v-row>

        <div class="mb-3" v-if="isGroceryTypeProduct">
          <p class="g-bold g-heading-xs g-text-color-black">
            Dietary Information
          </p>
          <template v-if="hasDietaryInformation">
            <v-row no-gutters>
              <v-col
                cols="4"
                v-for="dietary in filteredDietaryInformation"
                :key="dietary.displayName"
              >
                <span
                  ><v-icon color="green" class="p-icon mb-1" small
                    >mdi-check-bold</v-icon
                  >
                  {{ dietary.displayName }}</span
                >
              </v-col>
            </v-row>
          </template>
          <template v-else>
            <span>Not specified</span>
          </template>
        </div>

        <div class="mb-3" v-if="isLiquorTypeProduct">
          <v-row>
            <v-col cols="12" lg="6">
              <p class="g-bold g-heading-xs g-text-color-black">
                Specifications
              </p>
              <div class="mt-2">
                <div class="p-row-specifications">
                  <div class="p-col-left">Varietal</div>
                  <div class="p-col-right">
                    {{ item.liquorInformation.varietal || 'Not specified' }}
                  </div>
                </div>
                <div class="p-row-specifications">
                  <div class="p-col-left">Type</div>
                  <div class="p-col-right">
                    {{ item.liquorInformation.type || 'Not specified' }}
                  </div>
                </div>
                <div class="p-row-specifications">
                  <div class="p-col-left">Style</div>
                  <div class="p-col-right">
                    {{ item.liquorInformation.style || 'Not specified' }}
                  </div>
                </div>
                <div class="p-row-specifications">
                  <div class="p-col-left">Current Vintage</div>
                  <div class="p-col-right">
                    {{ item.liquorInformation.vintage || 'Not specified' }}
                  </div>
                </div>
                <div class="p-row-specifications">
                  <div class="p-col-left">Closure</div>
                  <div class="p-col-right">
                    {{ item.liquorInformation.closureType || 'Not specified' }}
                  </div>
                </div>
                <div class="p-row-specifications">
                  <div class="p-col-left">Alcohol Volume (%)</div>
                  <div class="p-col-right">
                    {{
                      item.liquorInformation.alcoholVolume || 'Not specified'
                    }}
                  </div>
                </div>
                <div class="p-row-specifications">
                  <div class="p-col-left">Standard Drinks</div>
                  <div class="p-col-right">
                    {{
                      item.liquorInformation.standardDrinks || 'Not specified'
                    }}
                  </div>
                </div>
                <div class="p-row-specifications">
                  <div class="p-col-left">Wine Body</div>
                  <div class="p-col-right">
                    {{ item.liquorInformation.wineBody || 'Not specified' }}
                  </div>
                </div>
                <div class="p-row-specifications">
                  <div class="p-col-left">Wine Marker</div>
                  <div class="p-col-right">
                    {{ item.liquorInformation.wineMaker || 'Not specified' }}
                  </div>
                </div>
                <div class="p-row-specifications">
                  <div class="p-col-left">Wine Sweetness</div>
                  <div class="p-col-right">
                    {{
                      item.liquorInformation.wineSweetness || 'Not specified'
                    }}
                  </div>
                </div>
                <div class="p-row-specifications">
                  <div class="p-col-left">Brewery</div>
                  <div class="p-col-right">
                    {{ item.liquorInformation.brewery || 'Not specified' }}
                  </div>
                </div>
                <div class="p-row-specifications">
                  <div class="p-col-left">Bottled In</div>
                  <div class="p-col-right">
                    {{ item.liquorInformation.bottledIn || 'Not specified' }}
                  </div>
                </div>
              </div>
            </v-col>
            <v-col cols="12" lg="6">
              <p class="g-bold g-heading-xs g-text-color-black">
                Awards & Recognitions
              </p>
              <div v-if="hasAwards" class="mt-2">
                <div
                  class="p-row-awards"
                  v-for="(award, index) in awardsList"
                  :key="index"
                >
                  <div class="p-col-left">{{ award.rating }} Points</div>
                  <div class="p-col-right">{{ award.ratedBy }}</div>
                </div>
              </div>
              <div v-else>Not specified</div>
            </v-col>
          </v-row>
        </div>

        <div v-if="!isServiceTypeProduct" class="mb-3">
          <p class="g-bold g-heading-xs g-text-color-black">
            Allergens / Warnings
          </p>
          <span>{{ item.allergens || 'Not specified' }}</span>
        </div>

        <div v-if="!isServiceTypeProduct" class="mb-3">
          <p class="g-bold g-heading-xs g-text-color-black">Product Keywords</p>
          <div v-if="hasKeywords">
            <span
              v-for="keyword in item.supplierKeywords"
              :key="keyword"
              class="py-2 py-md-4"
            >
              <v-chip class="ml-0 ma-1 pb-1" color="primary" small>
                {{ keyword }}
              </v-chip>
            </span>
          </div>
          <div v-else>
            <span>Not specified</span>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { buildCountryDisplayRules } from '@/utils/countryDisplay'
import { formatRegionDisplayName } from '@/utils/stateDisplay'

const PRODUCT_TYPES = {
  GROCERY: {
    value: 'GROCERY',
    text: 'Grocery & Fresh'
  },
  LIQUOR: {
    value: 'LIQUOR',
    text: 'Liquor'
  },
  SERVICE: { value: 'SERVICE', text: 'Service' }
}

const TARGET_TYPE = {
  GEOGRAPHY: 'All (Geography)',
  BANNER_GROUP: 'Banner Group(s)',
  MULTI_STORE_OPERATOR: 'Multi-Store Operator(s)',
  RETAILER: 'Retailer(s)'
}

export default {
  props: {
    item: {
      type: Object,
      required: true
    },

    pricingRegions: {
      type: Array,
      required: true
    }
  },

  data: () => ({
    ...TARGET_TYPE
  }),

  computed: {
    ...mapGetters('tenant', [
      'ownerControlledProductTradingTerms',
      'tenantCountryRegion'
    ]),

    awardsList() {
      if (this.isGroceryTypeProduct) return []
      // Remove non-awards properties as are not part of awards info
      return Object.values(this.item.liquorInformation.awards)
    },

    isGroceryTypeProduct() {
      return this.item.productType === PRODUCT_TYPES.GROCERY.value
    },

    isLiquorTypeProduct() {
      return this.item.productType === PRODUCT_TYPES.LIQUOR.value
    },

    hasAwards() {
      return this.awardsList.length > 0
    },

    hasDietaryInformation() {
      return this.filteredDietaryInformation.length > 0
    },

    hasKeywords() {
      return this.item.supplierKeywords.length > 0
    },

    hasRegionalPrices() {
      return (
        this.item.regionalPriceMap &&
        Object.keys(this.item.regionalPriceMap).length > 0
      )
    },

    filteredDietaryInformation() {
      return Object.values(this.item.dietaryInformation).filter(
        (obj) => obj.value === true
      )
    },

    sortedRegionalPriceMap() {
      return Object.assign(
        ...Object.entries(this.item.regionalPriceMap)
          .sort()
          .map(([key, value]) => {
            return { [key]: value }
          })
      )
    },

    targetType() {
      if (this.item.rangeRule?.targetBannerGroups?.length)
        return TARGET_TYPE.BANNER_GROUP
      else if (this.item.rangeRule?.targetMultiStoreOperators?.length)
        return TARGET_TYPE.MULTI_STORE_OPERATOR
      else if (this.item.rangeRule?.targetStores?.length)
        return TARGET_TYPE.RETAILER
      else if (this.item.rangeRule?.targetStorePricingRegions?.length)
        return TARGET_TYPE.GEOGRAPHY
      else return undefined
    },

    targetList() {
      if (this.targetType === TARGET_TYPE.BANNER_GROUP)
        return this.item.rangeRule.targetBannerGroups.map(
          (bg) => bg.bannerGroupName
        )
      else if (this.targetType === TARGET_TYPE.MULTI_STORE_OPERATOR)
        return this.item.rangeRule.targetMultiStoreOperators.map(
          (mso) => mso.multiStoreOperatorName
        )
      else if (this.targetType === TARGET_TYPE.RETAILER)
        return this.item.rangeRule.targetStores.map((store) => store.storeName)
      else return []
    },

    isServiceTypeProduct() {
      return this.item.productType === PRODUCT_TYPES.SERVICE.value
    },

    displayRules() {
      return buildCountryDisplayRules(this.tenantCountryRegion)
    },

    isUSCountry() {
      return this.displayRules.isUSCountry
    },

    formattedUsTaxCode() {
      const ticCode = this.item?.usTaxCode?.ticCode
      const label = this.item?.usTaxCode?.label
      if (ticCode && label) return `${ticCode} - ${label}`
      return ticCode || label || null
    }
  },

  methods: {
    getPricingRegionName(code) {
      return formatRegionDisplayName(
        this.pricingRegions.find((r) => r.code === code),
        this.tenantCountryRegion
      )
    },

    promoByStatus(activeOrFuturePromo = [], status) {
      return activeOrFuturePromo?.filter((promo) => promo.status === status)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#product-details {
  .g-heading-xs {
    font-size: 1.05rem;
  }
  .p-chip {
    font-weight: bold;
    letter-spacing: 0.25px;
    &-banner {
      color: white;
      background-color: #ff8200;
    }
    &-mso {
      color: white;
      background-color: #253d81;
    }
    &-retailer {
      color: white;
      background-color: #06a8f3;
    }
    &-product-type {
      color: black;
      background-color: hsl(82, 66%, 75%);
      letter-spacing: 0.5px;
    }
    &-family-group-promotion {
      a {
        color: $family-group-color;
      }
      border-color: $family-group-color;
    }

    &-single-product-promotion {
      a {
        color: $single-product-color;
      }
      border-color: $single-product-color;
    }
  }
  .p-icon {
    color: $secondary-color;
  }
  .p-row-awards {
    align-items: center;
    border-bottom: 2px solid #e0e0e0;
    display: flex;
    height: 30px;
    overflow: contain;
    min-width: 250px;
    max-width: 95%;
    .p-col-left {
      color: $blue-color;
      font-weight: bold;
      min-width: 75px;
      width: 25%;
    }
    .p-col-right {
      text-align: right;
      min-width: 175px;
      width: 75%;
    }
  }
  .p-row-specifications {
    align-items: center;
    border-bottom: 2px solid #e0e0e0;
    display: flex;
    height: 30px;
    overflow: contain;
    min-width: 250px;
    max-width: 95%;
    .p-col-left {
      color: dimgray;
      font-weight: bold;
      width: 50%;
    }
    .p-col-right {
      text-align: right;
      width: 50%;
    }
  }
  .p-zone-prices-table {
    width: 100% !important;
    margin-right: 8px;

    td {
      height: unset !important;
      padding: 4px !important;
    }
  }
}

@media only screen and (min-width: 960px) {
  #product-details {
    .p-right-column {
      border-left: 1px solid lightgray;
      padding-left: 16px;
    }

    .p-bottom-border {
      border-bottom: 1px solid lightgray;
    }
  }
}
</style>
